import { ThemeEvents } from '@theme/events';
import { Component } from '@theme/component';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

/**
 * @typedef {Object} ProductPriceRefs
 * @property {HTMLElement} priceContainer
 * @property {HTMLElement} [volumePricingNote]
 */

/**
 * A custom element that displays a product price.
 * This component listens for variant update events and updates the price display accordingly.
 * It handles price updates from two different sources:
 * 1. Variant picker (in quick add modal or product page)
 * 2. Swatches variant picker (in product cards)
 *
 * @extends {Component<ProductPriceRefs>}
 */
class ProductPrice extends Component {
  /** @type {WeakMap<HTMLScriptElement, {options: string[], variants: any[]}>} */
  #mdPriceCache = new WeakMap();

  /**
   * Update the visible price on input change, before Shopify's section
   * request completes. The native section response remains authoritative.
   * @param {Event} event
   */
  #handleImmediateVariantChange = (event) => {
    if (!(event.target instanceof Element)) return;
    const picker = event.target.closest('variant-picker[data-product-id]');
    if (!picker || !picker.contains(event.target)) return;
    const context = this.closest('.product-information');
    if (!context || !context.contains(picker)) return;
    if (picker.dataset.productId !== this.dataset.productId) return;
    const script = picker.querySelector('script[data-md-price-variants]');
    if (!(script instanceof HTMLScriptElement)) return;

    // Child products in a Shopify combined listing need their server content.
    const target = event.target instanceof HTMLSelectElement
      ? event.target.selectedOptions[0]
      : event.target;
    if (target?.dataset?.connectedProductUrl) {
      try {
        const oldPath = new URL(picker.dataset.productUrl || '', location.origin).pathname;
        const newPath = new URL(target.dataset.connectedProductUrl, location.origin).pathname;
        if (oldPath !== newPath) return;
      } catch {
        return;
      }
    }

    let map = this.#mdPriceCache.get(script);
    if (!map) {
      try {
        map = JSON.parse(script.textContent || '');
        if (!Array.isArray(map.options) || !Array.isArray(map.variants)) return;
        this.#mdPriceCache.set(script, map);
      } catch {
        return;
      }
    }
    if (typeof picker.getAllSelectedOptions !== 'function') return;
    const selection = new Map(picker.getAllSelectedOptions().map((option) => [option.name, option.value]));
    const optionValues = map.options.map((name) => selection.get(name));
    if (optionValues.some((value) => value == null)) return;
    const variant = map.variants.find((item) =>
      Array.isArray(item.options) &&
      item.options.length === optionValues.length &&
      item.options.every((value, index) => value === optionValues[index])
    );
    if (!variant || !variant.available || variant.hasVolume) return;

    const container = this.querySelector('[ref="priceContainer"]');
    if (!container) return;
    let mainPrice = container.querySelector('.price bdi');
    if (!mainPrice) {
      const newPrice = document.createElement('span');
      newPrice.className = 'price';
      mainPrice = document.createElement('bdi');
      newPrice.append(mainPrice);
      container.append(newPrice);
    }
    mainPrice.textContent = variant.price;
    const hasSale = Number(variant.compareCents) > Number(variant.priceCents);
    let compare = container.querySelector('.compare-at-price');
    if (!hasSale) {
      compare?.remove();
      return;
    }
    if (!compare) {
      compare = document.createElement('span');
      compare.className = 'price-item__group price-item--regular compare-at-price';
      const compareText = document.createElement('bdi');
      compare.append(compareText);
      container.append(compare);
    }
    let compareText = compare.querySelector('bdi');
    if (!compareText) {
      compareText = document.createElement('bdi');
      compare.append(compareText);
    }
    compareText.textContent = variant.compare;
  };

  connectedCallback() {
    super.connectedCallback();
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.addEventListener(StandardEvents.productSelect, this.#handleProductSelect);
    closestSection.addEventListener('change', this.#handleImmediateVariantChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.removeEventListener(StandardEvents.productSelect, this.#handleProductSelect);
    closestSection.removeEventListener('change', this.#handleImmediateVariantChange);
  }

  /**
   * Handles product select event and updates the price.
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html, newProduct } = detail;

        if (newProduct) {
          this.dataset.productId = newProduct.id;
        } else if (detail.productId && detail.productId !== this.dataset.productId) {
          return;
        }

        const { priceContainer, volumePricingNote } = this.refs;
        // Find the new product-price element in the updated HTML
        const newProductPrice = html.querySelector(`product-price[data-block-id="${this.dataset.blockId}"]`);
        if (!newProductPrice) return;

        // Update price container
        const newPrice = newProductPrice.querySelector('[ref="priceContainer"]');
        if (newPrice && priceContainer) {
          priceContainer.replaceWith(newPrice);
        }

        // Update volume pricing note
        const newNote = newProductPrice.querySelector('[ref="volumePricingNote"]');
        if (!newNote) {
          volumePricingNote?.remove();
        } else if (!volumePricingNote) {
          // Use newPrice since priceContainer was just replaced and now points to the detached element
          newPrice?.insertAdjacentElement('afterend', /** @type {Element} */ (newNote.cloneNode(true)));
        } else {
          volumePricingNote.replaceWith(newNote);
        }

        // Update installments (SPI banner) variant ID to trigger payment terms re-render
        const installmentsInput = /** @type {HTMLInputElement|null} */ (
          this.querySelector(`#product-form-installment-${this.dataset.blockId} input[name="id"]`)
        );
        if (installmentsInput) {
          installmentsInput.value = detail.resource?.id ?? '';
          installmentsInput.dispatchEvent(new Event('change', { bubbles: true }));
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[product-price] Event promise rejected:', error);
      });
  };
}

if (!customElements.get('product-price')) {
  customElements.define('product-price', ProductPrice);
}
